import React, { useState, useRef } from 'react';
import '../css/textArea.css';
import { ClearIcon } from '../../../../../assets-module/asset-configuration/icon/iconExporter/iconExporter';

export interface TextAreaProps {
  id?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  label?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onClear?: () => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  rows?: number;
  assistiveText?: string;
  error?: boolean;
  errorText?: string;
  warning?: boolean;
  maxLength?: number;
  showClearButton?: boolean;
}

const TextArea: React.FC<TextAreaProps> = ({
  id,
  name,
  value,
  placeholder,
  label,
  onChange,
  onFocus,
  onBlur,
  onClear,
  required = false,
  disabled = false,
  className = '',
  rows = 6,
  assistiveText,
  error = false,
  errorText,
  warning = false,
  maxLength,
  showClearButton = true,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const textareaId = id || `textarea-${Math.random().toString(36).slice(2, 11)}`; //NOSONAR

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    /* istanbul ignore next */
    if (textarea) {
      textarea.style.height = '0px';
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.max(scrollHeight, 120);
      textarea.style.height = `${newHeight}px`;
    }
  };

  React.useEffect(() => {
    adjustHeight();
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e);
    requestAnimationFrame(() => adjustHeight());
  };

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    adjustHeight();
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClear) {
      onClear();
    }
   else /* istanbul ignore next */ if (onChange) {
      const syntheticEvent = {
        target: { name, value: '' }
      } as React.ChangeEvent<HTMLTextAreaElement>;
      onChange(syntheticEvent);
    }
    textareaRef.current?.focus();
    requestAnimationFrame(() => adjustHeight());
  };

  const displayLabel = label || placeholder;
  const hasValue = value && value.length > 0;
  const showFloatingLabel = isFocused || hasValue;
  const showClear = showClearButton && hasValue && isFocused;

  const hasAsterisk = displayLabel?.includes('*');
  const labelText = hasAsterisk ? displayLabel?.replace(' *', '').replace('*', '') : displayLabel;

  const renderLabel = () => {
     /* istanbul ignore next */
    if (!displayLabel) return null;
    return (
      <>
        {labelText}
        {hasAsterisk && <span className="scl--web-component-textarea-required-asterisk"> *</span>}
      </>
    );
  };

  return (
    <div className={`scl--web-component-textarea-container ${className}`}>
      <label
        className={`scl--web-component-textarea-input-plate ${error ? 'error' : ''} ${warning ? 'warning' : ''} ${disabled ? 'disabled' : ''} ${isFocused ? 'focused' : ''}`}
        htmlFor={textareaId}
      >
        {/* ✅ Only floating label - no placeholder */}
        {displayLabel && (
          <span className={`scl--web-component-textarea-label ${showFloatingLabel ? 'floating' : ''}`}>
            {renderLabel()}
          </span>
        )}

        <div className="scl--web-component-textarea-input-wrapper">
          <div className="scl--web-component-textarea-value-wrapper">
            <div className="scl--web-component-textarea-text-wrapper">
              <textarea
                ref={textareaRef}
                id={textareaId}
                name={name}
                value={value}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                required={required}
                disabled={disabled}
                rows={rows}
                maxLength={maxLength}
                autoComplete="off"
                className="scl--web-component-textarea-input"
              />
            </div>
          </div>

          {showClear ? (
            <button
              type="button"
              className="scl--web-component-textarea-clear-button"
              onMouseDown={handleClear}
              tabIndex={-1}
              aria-label="Clear input"
            >
              <ClearIcon width={26} height={26} />
            </button>
          ) : (
            <button
              type="button"
              className="scl--web-component-textarea-clear-button hidden"
              tabIndex={-1}
              aria-label="Clear input"
              aria-hidden="true"
            >
              <ClearIcon width={26} height={26} />
            </button>
          )}
        </div>
      </label>

      <div className="scl--web-component-textarea-footer">
        {(assistiveText || errorText) && (
          <div className={`scl--web-component-textarea-assistive ${error ? 'error' : ''}`}>
            {error && errorText ? errorText : assistiveText}
          </div>
        )}
        
        {maxLength && (
          <div className="scl--web-component-textarea-character-count">
            {value?.length || 0}/{maxLength}
          </div>
        )}
      </div>
    </div>
  );
};

export default TextArea;